import CustomTabs from "@/shared/components/common/tab";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import { Button, CircularProgress, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import FirstTab from "./firstTab";
import JobQualificationsSecondTab from "./JobQualificationsSecondTab";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import RequestedCreditAmountThirdTab from "./requestedCreditAmountThirdTab";
import AwaitingApproval from "./awaitApproval";
import { CreditDataMain } from "@/types/api/user/home.type.data";
import { toast } from "react-toastify";
import { fileToBase64 } from "@/hooks/base64";
import { useMutation } from "@tanstack/react-query";
import { postRequestCreditOrder } from "@/api/user/home";
import { AxiosError } from "axios";
/**
 * TabInformationReview
 *
 * کامپوننت مدیریت روند بررسی اطلاعات اعتباری کاربر در سه مرحله:
 * 1. اطلاعات و مدارک فردی
 * 2. اطلاعات و مدارک شغلی
 * 3. تایید نهایی درخواست اعتبار
 *
 * ویژگی‌ها:
 * - استفاده از CustomTabs برای نمایش مراحل مختلف.
 * - مدیریت وضعیت تب‌ها و فعال/غیرفعال بودن آن‌ها با useState و useEffect.
 * - تبدیل فایل‌های آپلود شده به Base64 قبل از ارسال به سرور.
 * - استفاده از React Query برای ارسال اطلاعات به API با handleFinalSubmit و fetchRequestResetPass.
 * - نمایش پیام‌های موفقیت و خطا با toast.
 *
 * @param {Object} props
 * @param {CreditDataMain} props.userInquiry اطلاعات اعتباری کاربر
 *
 * @component
 * @returns JSX.Element
 */

const TabInformationReview = ({ userInquiry }: { userInquiry: CreditDataMain; }) => {
  const theme = useTheme();
  const [tabEnable, setTabEnable] = useState("firstTab");
  const [secondTabEnabled, setSecondTabEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [successTab, setSuccessTab] = useState(false);
  const [successSecondTab, setSuccessSecondTab] = useState(false);
  const [successThirdTab, setSuccessThirdTab] = useState(false);
  const [isContent, setIsContent] = useState(false);
  const [finalStateSubmit, setFinalStateSubmit] = useState(false);
  const [address, setAddress] = useState("");
  const [uploadedImages, setUploadedImages] = useState({ cardFront: null, cardBack: null, birthCert: null, });
  const [uploadImageJob, setUploadImageJob] = useState(null);
  const [uploadImageElseJob, setUploadImageElseJob] = useState(null);
  const [uploadImageCheckbookImage, setUploadImageCheckbookImage] = useState(null);
  const [valuePriceSlider, setValuePriceSlider] = useState<any>(0);
  const [valueMonthSlider, setValueMonthSlider] = useState<any>(+userInquiry?.min_month);
  const [job, setJob] = useState({
    company_name: "",
    company_title: "",
    company_nid: "",
    postal_code: "",
    company_address: "",
  });
  const [freelanceJob, setFreelanceJob] = useState({
    company_name: "",
    company_title: "",
    postal_code: "",
    company_address: "",
    company_nid: "",
  });

  const [privateEmployee, setPrivateEmployee] = useState(null);
  const [governmentEmployeeCountry, setGovernmentEmployeeCountry] = useState(null);

  const { mutate: fetchRequestResetPass, isPending } = useMutation({
    mutationFn: postRequestCreditOrder,
    onSuccess: (data: any) => {
      window.location.reload()
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message);
    },
  });

  const [selectedOption, setSelectedOption] = useState<string>(
    "کارمند بخش دولتی (لشکری)"
  );

  const uploadedImagesRef = useRef<any>({});
  const uploadImageJobRef = useRef<any>({});
  const uploadImageElseJobRef = useRef<any>({});
  const stateJobRef = useRef<any>({});
  const addressRef = useRef<any>({});
  const stateFreelanceJobRef = useRef<any>({});
  const uploadImageCheckRef = useRef<any>({});

  useEffect(() => {
    uploadedImagesRef.current = uploadedImages;
    addressRef.current = address;
    uploadImageJobRef.current = uploadImageJob;
    uploadImageElseJobRef.current = uploadImageElseJob;
    uploadImageCheckRef.current = uploadImageCheckbookImage;
    stateJobRef.current = job;
    stateFreelanceJobRef.current = freelanceJob;
  }, [
    uploadedImages,
    address,
    uploadImageJob,
    selectedOption,
    job,
    freelanceJob,
    uploadImageElseJob,
    uploadImageCheckbookImage,
  ]);

  const handleSubmit = () => {
    const finalImages = uploadedImagesRef.current;
    const addressForm = addressRef.current;

    // Specific error messages for each missing field
    if (!addressForm) {
      toast.error("لطفاً آدرس محل سکونت را وارد کنید");
      return;
    }
    if (!finalImages.cardFront) {
      toast.error("لطفاً عکس روی کارت ملی را آپلود کنید");
      return;
    }
    if (!finalImages.cardBack) {
      toast.error("لطفاً عکس پشت کارت ملی را آپلود کنید");
      return;
    }
    if (!finalImages.birthCert) {
      toast.error("لطفاً عکس صفحه اول شناسنامه را آپلود کنید");
      return;
    }

    // All validations passed
    setSecondTabEnabled(true);
    setActiveTab(1);
    setSuccessThirdTab(false);
    setSuccessTab(true);
    toast.success("اطلاعات با موفقیت ثبت شد");
  };

  const handleContinue = () => {
    const fileJob = uploadImageJobRef.current;
    const valueJob = stateJobRef.current;
    const valueFreelanceJob = stateFreelanceJobRef.current;
    const fileElseJob = uploadImageElseJobRef.current;

    const requiresFileUpload = ["کارمند بخش دولتی (لشکری)", "کارمند بخش دولتی (کشوری)"].includes(selectedOption);

    if (requiresFileUpload) {
      if (!fileJob) {
        toast.error("لطفاً فایل مربوط به مدرک شغلی را آپلود کنید");
        return;
      }
      // All validations passed
      setActiveTab(2);
      setSuccessThirdTab(true);
      setSuccessSecondTab(true);
      toast.success("اطلاعات با موفقیت ثبت شد");
    }

    if (selectedOption === "کارمند بخش خصوصی") {
      if (!valueJob.company_name) {
        toast.error("لطفاً نام شرکت را وارد کنید");
        return;
      }
      if (!valueJob.company_title) {
        toast.error("لطفاً عنوان شغلی را وارد کنید");
        return;
      }
      if (!valueJob.company_nid) {
        toast.error("لطفاً شناسه ملی شرکت را وارد کنید");
        return;
      }
      if (!valueJob.postal_code) {
        toast.error("لطفاً کد پستی را وارد کنید");
        return;
      }
      if (!valueJob.company_address) {
        toast.error("لطفاً آدرس شرکت را وارد کنید");
        return;
      }
      if (!fileJob) {
        toast.error("لطفاً فایل مربوط به مدرک شغلی را آپلود کنید");
        return;
      }
      // All validations passed
      setActiveTab(2);
      setSuccessThirdTab(true);
      setSuccessSecondTab(true);
      toast.success("اطلاعات با موفقیت ثبت شد");
    }

    if (selectedOption === "صاحبین مشاغل آزاد") {
      if (!valueFreelanceJob.company_name) {
        toast.error("لطفاً نام شرکت/مغازه را وارد کنید");
        return;
      }
      if (!valueFreelanceJob.company_title) {
        toast.error("لطفاً عنوان شغلی را وارد کنید");
        return;
      }
      if (!valueFreelanceJob.company_nid) {
        toast.error("لطفاً شناسه ملی شرکت/مغازه را وارد کنید");
        return;
      }
      if (!valueFreelanceJob.postal_code) {
        toast.error("لطفاً کد پستی را وارد کنید");
        return;
      }
      if (!valueFreelanceJob.company_address) {
        toast.error("لطفاً آدرس شرکت/مغازه را وارد کنید");
        return;
      }
      if (!fileJob) {
        toast.error("لطفاً فایل مربوط به مدرک شغلی را آپلود کنید");
        return;
      }
      if (!fileElseJob) {
        toast.error("لطفاً فایل مربوط به مدرک اجاره یا سند ملک را آپلود کنید");
        return;
      }
      // All validations passed
      setActiveTab(2);
      setSuccessThirdTab(true);
      setSuccessSecondTab(true);
      toast.success("اطلاعات با موفقیت ثبت شد");
    }
  };

  const handleFinalSubmit = async () => {
    if (!valuePriceSlider || valuePriceSlider === 0) {
      toast.error("لطفاً مبلغ درخواستی را انتخاب کنید");
      return;
    }
    if (!valueMonthSlider || valueMonthSlider === 0) {
      toast.error("لطفاً مدت زمان بازپرداخت را انتخاب کنید");
      return;
    }
    const finalImages = uploadedImagesRef.current;
    const addressForm = addressRef.current;
    const fileImageJob = uploadImageJobRef.current;
    const valueFreelanceJob = stateFreelanceJobRef.current;
    const fileElseJob = uploadImageElseJobRef.current;
    const fileCheckJob = uploadImageCheckRef.current;
    const safeFileToBase64 = async (file?: File | null) => {
      if (!file) return null;
      return await fileToBase64(file);

    };
    const birthCertBase64 = finalImages?.birthCert
      ? await fileToBase64(finalImages.birthCert)
      : null;

    const nidFrontBase64 = finalImages?.cardFront
      ? await fileToBase64(finalImages.cardFront)
      : null;

    const nidBackBase64 = finalImages?.cardBack
      ? await fileToBase64(finalImages.cardBack)
      : null;

    const fileImageJobBase64 = fileImageJob
      ? await fileToBase64(fileImageJob)
      : null;

    const fileImageCheckBase64 = fileCheckJob
      ? await safeFileToBase64(fileCheckJob)
      : null;

    const fileImageElseJobBase64 = fileElseJob
      ? await safeFileToBase64(fileElseJob)
      : null;

    const mainRequestBody = {
      address: addressForm,
      amount: valuePriceSlider,
      birth_certificate_image: birthCertBase64,
      checkbook_image: fileImageCheckBase64,
      last_name: userInquiry?.last_name,
      month: valueMonthSlider,
      name: userInquiry?.name,
      nid_back_image: nidBackBase64,
      nid_front_image: nidFrontBase64,
    };

    const valueJob = stateJobRef.current;

    const requiresFileUpload = ["کارمند بخش دولتی (لشکری)", "کارمند بخش دولتی (کشوری)",].includes(selectedOption);

    if (requiresFileUpload) {
      const requestBody = {
        ...mainRequestBody,
        job_id: selectedOption === "کارمند بخش دولتی (لشکری)" ? 1 : 2,
        job_image: fileImageJobBase64,
      };
      fetchRequestResetPass(requestBody);
    }

    if (selectedOption === "کارمند بخش خصوصی") {
      const requestBody = {
        ...mainRequestBody,
        company_address: valueJob?.company_address,
        company_name: valueJob.company_name,
        company_nid: valueJob.company_nid,
        company_title: valueJob.company_title,
        job_id: 3,
        job_image: fileImageJobBase64,
        postal_code: valueJob.postal_code,
      };
      fetchRequestResetPass(requestBody);
      toast.success("اطلاعات با موفقیت ثبت شد");
    }

    if (selectedOption === "صاحبین مشاغل آزاد") {
      const requestBody = {
        ...mainRequestBody,
        company_address: valueFreelanceJob.company_address,
        company_name: valueFreelanceJob.company_name,
        company_nid: valueFreelanceJob.company_nid,
        company_title: valueFreelanceJob.company_title,
        job_id: 4,
        job_image: fileImageJobBase64,
        lease_image: fileImageElseJobBase64,
        postal_code: valueFreelanceJob.postal_code,
      };
      fetchRequestResetPass(requestBody);
    }
  };

  const tabData = useMemo(
    () => [
      {
        label: "مرحله اول: اطلاعات و مدارک فردی",
        content: (
          <StackKit direction={"column"} sx={{ width: "100%", alignItems: "flex-start", height: "100%", justifyContent: "space-between", }}>
            <FirstTab userInquiry={userInquiry} setUploadedImages={setUploadedImages} setAddress={setAddress} address={address} />
            <StackKit direction={"row"} justifyContent={"end"} width={"100%"}>
              <Button onClick={handleSubmit} variant="contained" sx={{ mt: 3, width: "fit-content" }}>
                مرحله بعد
              </Button>
            </StackKit>
          </StackKit>
        ),
        successText: "",
        disabled: false,
      },
      {
        label: "مرحله دوم : اطلاعات و مدارک شغلی",
        content: (
          <StackKit direction={"column"} sx={{ width: "100%", alignItems: "flex-start", height: "100%", justifyContent: "space-between", }}>
            <JobQualificationsSecondTab
              setUploadedImages={setUploadedImages}
              setSelectedOption={setSelectedOption}
              selectedOption={selectedOption}
              job={job}
              setJob={setJob}
              setPrivateEmployee={setPrivateEmployee}
              setGovernmentEmployeeCountry={setGovernmentEmployeeCountry}
              setFreelanceJob={setFreelanceJob}
              setUploadImageJob={setUploadImageJob}
              setUploadImageElseJob={setUploadImageElseJob}
            />
            <StackKit direction={"row"} justifyContent={"space-between"} width={"100%"}>
              <Button onClick={handleContinue} variant="contained" sx={{ mt: 3, width: "fit-content" }}>
                مرحله قبل
              </Button>
              <Button onClick={handleContinue} variant="contained" sx={{ mt: 3, width: "fit-content" }}>
                مرحله بعد
              </Button>
            </StackKit>
          </StackKit>
        ),
        successText: "",
        disabled: !secondTabEnabled,
      },
      { label: "مرحله سوم : تایید نهایی",
        content: (
          <StackKit direction={"column"} sx={{ width: "100%", alignItems: "flex-start", height: "100%", justifyContent: "space-between", }}>
            <RequestedCreditAmountThirdTab
              max_price={userInquiry?.max_price}
              step_month={userInquiry?.step_month}
              step_price={userInquiry?.step_price}
              min_price={userInquiry?.min_price}
              min_month={userInquiry?.min_month}
              max_month={userInquiry?.max_month}
              setUploadImageCheckbookImage={setUploadImageCheckbookImage}
              valueMonthSlider={valueMonthSlider}
              valuePriceSlider={valuePriceSlider}
              setValueMonthSlider={setValueMonthSlider}
              setValuePriceSlider={setValuePriceSlider}
            />
            <StackKit direction={"row"} justifyContent={"space-between"} width={"100%"}>
              <Button variant="contained" sx={{ mt: 3, width: "fit-content" }}
                onClick={() => {
                  setSecondTabEnabled(true);
                  setActiveTab(1);
                  setSuccessThirdTab(false);
                  setSuccessTab(true);
                }}>
                مرحله قبل
              </Button>
              <CustomButton onClick={handleFinalSubmit} variant="contained" sx={{ mt: 3, width: isPending ? "12%" : "fit-content", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {isPending ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <TypoGraphyKit variant="body1">
                    مرحله بعد
                  </TypoGraphyKit>
                )}
              </CustomButton>
            </StackKit>
          </StackKit>
        ),
        successText: "",
        disabled: !successThirdTab,
      },
    ],
    [secondTabEnabled, successThirdTab, valueMonthSlider, valuePriceSlider, isPending, userInquiry]
  );
  return (
    <>
      {finalStateSubmit ? (
        <AwaitingApproval />
      ) : (
        <CustomTabs tabs={tabData} successTab={successTab} activeTab={activeTab} successSecondTab={successSecondTab} isContent={isContent}/>
      )}
    </>
  );
};

export default TabInformationReview;
